import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NlimsDataTable from '../shared/NlimsDataTable/NlimsDataTable';
import { marketPricesApi } from '../../api/marketPricesApi';
import { useMarketPriceReferenceOptions } from './useMarketTradReferences';
import {
  labelForValue,
  COMMODITY_FORM_OPTIONS,
  PRICE_TYPE_OPTIONS,
  PRICE_UNIT_OPTIONS,
  normalizeSeasonalFlagValue,
  seasonalFlagLabel,
} from './marketTradOptions';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { ActionDropdown, anchorRect, RejectNotesModal, DeleteConfirmModal, WORKFLOW_RULES } from '../shared/RowActionMenu';

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseError(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function normalizePriceRow(row = {}, marketLookup, speciesLookup) {
  const marketUuid = row.market_uuid ?? row.market_id ?? row.marketId ?? row.marketUuid ?? row.market?.uuid ?? '';
  const speciesUuid = row.species_uuid ?? row.speciesUuid ?? row.species?.uuid ?? '';
  const speciesCode = row.species_code ?? row.speciesCode ?? row.species?.code ?? '';
  const marketLabel =
    marketLookup.get(String(marketUuid)) || row.market_name || row.marketName || marketUuid;
  const speciesLabel =
    speciesLookup.get(String(speciesUuid)) ||
    speciesLookup.get(String(speciesCode)) ||
    row.species_name ||
    speciesCode ||
    speciesUuid;

  const priceType = row.price_type ?? row.priceType ?? '';
  const commodityForm = row.commodity_form ?? row.commodityForm ?? '';
  const priceUnit = row.price_unit ?? row.priceUnit ?? '';
  const seasonalFlag = normalizeSeasonalFlagValue(row.seasonal_flag ?? row.seasonalFlag ?? '');
  const gradeQuality = row.grade_quality ?? row.gradeQuality ?? '';
  const volumeTraded = Number(row.volume_traded ?? row.volumeTraded ?? 0);
  const dataStatus = row.data_status ?? row.validation_status ?? row.workflow_status ?? 'draft';

  return {
    ...row,
    id: row.uuid ?? row.id ?? null,
    uuid: row.uuid ?? row.id ?? null,
    market_uuid: marketUuid,
    market_label: marketLabel,
    species_uuid: speciesUuid,
    species_code: speciesCode,
    species_label: speciesLabel,
    price_ngn: Number(row.price_ngn ?? row.priceNgn ?? 0),
    price_type: priceType,
    price_type_label: labelForValue(PRICE_TYPE_OPTIONS, priceType),
    commodity_form: commodityForm,
    commodity_form_label: labelForValue(COMMODITY_FORM_OPTIONS, commodityForm) || commodityForm,
    price_unit: priceUnit,
    price_unit_label: labelForValue(PRICE_UNIT_OPTIONS, priceUnit) || priceUnit,
    price_date: row.price_date ?? row.priceDate ?? '',
    data_source: row.data_source ?? row.dataSource ?? '',
    seasonal_flag: seasonalFlag,
    seasonal_flag_label: seasonalFlagLabel(seasonalFlag),
    grade_quality: gradeQuality,
    grade_quality_label: gradeQuality,
    volume_traded: volumeTraded,
    volume_traded_label: volumeTraded,
    notes: row.notes ?? '',
    state: row.state ?? '',
    lga: row.lga ?? '',
    data_status: dataStatus || 'draft',
  };
}

function buildSpeciesLookup(speciesOptions = []) {
  const map = new Map();
  speciesOptions.forEach((option) => {
    const raw = option.raw || {};
    [option.value, raw.uuid, raw.id, raw.code, raw.species_code].filter(Boolean).forEach((key) => {
      map.set(String(key), option.label);
    });
  });
  return map;
}

function buildMarketLookup(marketOptions = []) {
  const map = new Map();
  marketOptions.forEach((option) => {
    const raw = option.raw || {};
    [option.value, raw.uuid, raw.id].filter(Boolean).forEach((key) => {
      map.set(String(key), option.label);
    });
  });
  return map;
}

// ── DataTables HTML helpers ───────────────────────────────────────────────────

function escHtml(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const SPECIES_BADGE_STYLE = {
  Broiler: 'background:#d1fae5;color:#065f46;',
  Cattle:  'background:#dbeafe;color:#1e40af;',
  Goat:    'background:#fef3c7;color:#92400e;',
  Sheep:   'background:#ede9fe;color:#5b21b6;',
  Turkey:  'background:#fed7aa;color:#92400e;',
  Layer:   'background:#fef9c3;color:#713f12;',
  Pig:     'background:#fce7f3;color:#9d174d;',
};

function speciesBadgeHtml(species) {
  if (!species) return '—';
  const style = SPECIES_BADGE_STYLE[species] || 'background:#f3f4f6;color:#374151;';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:9999px;font-size:11px;font-weight:600;${style}">${escHtml(species)}</span>`;
}

function statusBadgeHtml(status) {
  if (!status) return '';
  const map = {
    draft:     'background:#f5f5f4;color:#888780;border:1px solid #d6d3d1;',
    submitted: 'background:#eff6ff;color:#185FA5;border:1px solid #bfdbfe;',
    validated: 'background:#fffbeb;color:#BA7517;border:1px solid #fde68a;',
    approved:  'background:#f0fdf4;color:#3B6D11;border:1px solid #bbf7d0;',
    rejected:  'background:#fff1f2;color:#A32D2D;border:1px solid #fecdd3;',
  };
  const s = map[String(status).toLowerCase()] || map.draft;
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:9999px;font-size:11px;font-weight:600;letter-spacing:.02em;white-space:nowrap;${s}">${escHtml(status)}</span>`;
}

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return `₦${amount.toLocaleString('en-NG')}`;
}

function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount.toLocaleString('en-NG');
}

function formatBooleanText(value) {
  if (value === true) return 'true';
  if (value === false) return 'false';
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function detailFieldHtml(label, value, { raw = false, valueClass = '' } = {}) {
  const isEmpty = value === null || value === undefined || value === '';
  const display = isEmpty ? '—' : (raw ? String(value) : escHtml(value));
  return `
    <div class="nlims-market-child__field">
      <span class="nlims-market-child__label">${escHtml(label)}</span>
      <div class="nlims-market-child__value${valueClass ? ` ${valueClass}` : ''}">${display}</div>
    </div>
  `;
}

function expandActionCellHtml() {
  return '<button type="button" class="nlims-action-btn nlims-market-expand-btn" data-action="expand" aria-expanded="false" title="View more details">View More <span class="nlims-action-caret" aria-hidden="true">&#9660;</span></button>';
}

// Per-role status-transition map — also passed as the shared ActionDropdown's
// `transitions` prop (Submit/Validate/Approve/Reject gating is entirely
// driven by which target statuses appear here). R01 may act at every stage;
// R02 (national reviewer) only reviews an already-Validated record; R03
// (federal officer) reviews Submitted/Validated; R04/R05 (originator roles)
// may only submit a Draft/Rejected record.
const ROLE_WORKFLOW_TRANSITIONS = {
  R01: {
    draft: ['submitted'],
    submitted: ['validated', 'rejected'],
    validated: ['approved', 'rejected'],
    approved: ['rejected'],
    rejected: ['submitted'],
  },
  R02: {
    validated: ['approved', 'rejected'],
  },
  R03: {
    submitted: ['validated', 'rejected'],
    validated: ['rejected'],
  },
  R04: {
    draft: ['submitted'],
    rejected: ['submitted'],
  },
  R05: {
    draft: ['submitted'],
    rejected: ['submitted'],
  },
};

// R01/R04 may delete a price record at any status; R02/R03/R05/R06 never can
// — matches the original `canDeleteRecord` rule exactly (not status-gated).
const PRICE_DELETE_STATUSES = ['draft', 'submitted', 'validated', 'approved', 'rejected'];
const PRICE_ROLE_CAN = {
  R01: { edit: true, submit: true, validate: true, approve: true, reject: true, delete: true },
  R02: { edit: true, submit: true, validate: true, approve: true, reject: true, delete: false },
  R03: { edit: true, submit: true, validate: true, approve: true, reject: true, delete: false },
  R04: { edit: true, submit: true, validate: true, approve: true, reject: true, delete: true },
  R05: { edit: true, submit: true, validate: true, approve: true, reject: true, delete: false },
  R06: { edit: false, submit: false, validate: false, approve: false, reject: false, delete: false },
};

// "Edit Record" (which really means "open the record — view or act on it,
// depending on role/status") is shown whenever the role could do *something*
// with the record at that status, mirroring the old modal's `canAct` rule.
const PRICE_EDITABLE_STATUSES = {
  R01: ['draft', 'submitted', 'validated', 'approved', 'rejected'],
  R02: ['validated'],
  R03: ['submitted', 'validated'],
  R04: ['draft', 'rejected'],
  R05: ['draft', 'rejected'],
};

// ── Table columns — matches DCIC-03 form fields exactly ───────────────────────

function buildMarketPriceChildRowHtml(row = {}, roleId = '') {
  const status = String(row.data_status ?? 'draft').toLowerCase();
  const parentId = escHtml(row.uuid ?? row.id ?? '');
  const canEdit = !!(PRICE_ROLE_CAN[roleId]?.edit && (PRICE_EDITABLE_STATUSES[roleId] ?? []).includes(status));

  const viewButton = `
    <button type="button" class="nlims-market-child__action" data-child-action="view" data-parent-id="${parentId}">
      View Details
    </button>
  `;
  const editButton = canEdit
    ? `
      <button type="button" class="nlims-market-child__action nlims-market-child__action--primary" data-child-action="edit" data-parent-id="${parentId}">
        Edit Record
      </button>
    `
    : '';

  return `
    <div class="nlims-market-child">
      <div class="nlims-market-child__grid">
        <div class="nlims-market-child__col">
          ${detailFieldHtml('Market', row.market_label)}
          ${detailFieldHtml('Commodity Form', row.commodity_form_label || row.commodity_form)}
          ${detailFieldHtml('Seasonal Flag', formatBooleanText(row.seasonal_flag))}
          ${detailFieldHtml('State', row.state)}
        </div>
        <div class="nlims-market-child__col">
          ${detailFieldHtml('Price Date', row.price_date)}
          ${detailFieldHtml('Price (₦)', formatCurrency(row.price_ngn))}
          ${detailFieldHtml('Volume Traded', formatNumber(row.volume_traded))}
          ${detailFieldHtml('LGA', row.lga)}
        </div>
        <div class="nlims-market-child__col">
          ${detailFieldHtml('Workflow Status', row.data_status || 'draft')}
          ${detailFieldHtml('Price Unit', row.price_unit_label || row.price_unit)}
          ${detailFieldHtml('Grade / Quality', row.grade_quality)}
          ${detailFieldHtml('Notes', row.notes)}
        </div>
        <div class="nlims-market-child__col">
          ${detailFieldHtml('Species', row.species_label || row.species_code || row.species_name)}
          ${detailFieldHtml('Price Type', row.price_type_label || row.price_type)}
          ${detailFieldHtml('Data Source', row.data_source)}
        </div>
      </div>
      <div class="nlims-market-child__footer">
        ${viewButton}
        ${editButton}
      </div>
    </div>
  `;
}

const TABLE_COLUMNS = [
  { title: 'Market', data: 'market_label', sortable: true },
  { title: 'Price Date', data: 'price_date', sortable: true },
  {
    title: 'Species', data: 'species_label', sortable: true,
    render: (d, t) => (t === 'display' ? speciesBadgeHtml(d) : (d ?? '')),
  },
  { title: 'Commodity Form', data: 'commodity_form_label', sortable: true, defaultContent: '—' },
  {
    title: 'Price (₦)', data: 'price_ngn', sortable: true,
    render: (d, t) => (t === 'display' ? (d ? '₦' + Number(d).toLocaleString('en-NG') : '—') : (d ?? 0)),
  },
  { title: 'Price Unit', data: 'price_unit_label', sortable: true, defaultContent: '—' },
  { title: 'Price Type', data: 'price_type_label', sortable: true, defaultContent: '—' },
  {
    title: 'Seasonal Flag',
    data: 'seasonal_flag_label',
    sortable: true,
    defaultContent: '—',
    render: (d, t, row) => (t === 'display' ? (d || row.seasonal_flag || '—') : (row.seasonal_flag ?? '')),
  },
  {
    title: 'Volume Traded', data: 'volume_traded', sortable: true,
    render: (d, t) => (t === 'display' ? (d != null ? Number(d).toLocaleString('en-NG') : '—') : (d ?? 0)),
  },
  { title: 'Grade / Quality', data: 'grade_quality', sortable: true, defaultContent: '—' },
  { title: 'Data Source', data: 'data_source', sortable: true, defaultContent: '—' },
  { title: 'State', data: 'state', sortable: true, defaultContent: '—' },
  {
    title: 'Status', data: 'data_status', sortable: true,
    render: (d, t) => (t === 'display' ? statusBadgeHtml(d || 'draft') : (d ?? 'draft')),
  },
  {
    title: 'Action', data: null, sortable: false,
    render: (_d, type) => (type === 'display' ? expandActionCellHtml() : ''),
  },
];

// ── Filter select primitive ───────────────────────────────────────────────────

const SEL =
  'h-10 min-w-[150px] rounded-lg border border-gray-300 bg-white px-3 pr-8 text-sm ' +
  'text-gray-700 appearance-none cursor-pointer focus:outline-none focus:border-green-500';

function FilterSelect({ value, onChange, options, defaultLabel }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} className={SEL}>
        <option value="">{defaultLabel}</option>
        {options.map((o) => {
          const v = typeof o === 'string' ? o : String(o.value ?? o);
          const l = typeof o === 'string' ? o : String(o.label ?? v);
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MarketPrices({ onCreateNew, refreshKey = 0 }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pushWorkflowNotification } = useNotifications();
  const roleId = user?.roleId ?? '';
  const isR05 = roleId === 'R05';

  const [draftState,  setDraftState]  = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [search,      setSearch]      = useState('');
  const [actionMenu,   setActionMenu]   = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quickBusy,    setQuickBusy]    = useState('');

  const [records,        setRecords]        = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [fetchError,     setFetchError]     = useState('');

  const {
    marketOptions, speciesOptions,
    loading: refLoading, error: refError,
  } = useMarketPriceReferenceOptions();

  const marketLookup  = useMemo(() => buildMarketLookup(marketOptions),   [marketOptions]);
  const speciesLookup = useMemo(() => buildSpeciesLookup(speciesOptions), [speciesOptions]);

  // Refs so fetch effect always uses latest lookups without triggering extra re-fetches
  const marketLookupRef  = useRef(marketLookup);
  const speciesLookupRef = useRef(speciesLookup);
  useEffect(() => { marketLookupRef.current  = marketLookup;  }, [marketLookup]);
  useEffect(() => { speciesLookupRef.current = speciesLookup; }, [speciesLookup]);

  // ── Fetch backend records ─────────────────────────────────────────────────

  const loadRecords = useCallback(() => {
    let active = true;
    setLoadingRecords(true);
    setFetchError('');
    marketPricesApi
      .listAll({ per_page: 500 })
      .then((payload) => {
        if (!active) return;
        setRecords((payload.items || []).map((r) =>
          normalizePriceRow(r, marketLookupRef.current, speciesLookupRef.current)
        ));
      })
      .catch((err) => {
        if (!active) return;
        setFetchError(parseError(err, 'Failed to load market prices.'));
      })
      .finally(() => { if (active) setLoadingRecords(false); });
    return () => { active = false; };
  }, []); // refs used inside — no stale closure risk

  useEffect(() => {
    if (refLoading) return;
    return loadRecords();
  }, [refLoading, refreshKey, loadRecords]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const stateOptions = useMemo(
    () => [...new Set(records.map((r) => r.state).filter(Boolean))].sort(),
    [records],
  );

  const displayRecords = useMemo(() => {
    if (!stateFilter) return records;
    return records.filter((r) =>
      String(r.state ?? '').toLowerCase() === stateFilter.toLowerCase()
    );
  }, [records, stateFilter]);

  const renderMarketPriceChildRow = useCallback(
    (row) => buildMarketPriceChildRowHtml(row, roleId),
    [roleId],
  );

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleApply = useCallback(() => {
    setStateFilter(draftState);
  }, [draftState]);

  // Every transition goes through its dedicated backend endpoint.
  const handleQuickStatusSave = useCallback(async (row, status, notes = '') => {
    setFetchError('');
    setQuickBusy(
      status === 'submitted' ? 'Submitting...' :
      status === 'validated' ? 'Validating...' :
      status === 'approved'  ? 'Approving...' :
      status === 'rejected'  ? 'Rejecting...' :
      'Saving...'
    );
    try {
      if (status === 'submitted') await marketPricesApi.submit(row.uuid);
      else if (status === 'validated') await marketPricesApi.validate(row.uuid);
      else if (status === 'approved') await marketPricesApi.approve(row.uuid);
      else if (status === 'rejected') await marketPricesApi.reject(row.uuid, notes);
      setRecords((prev) =>
        prev.map((r) =>
          r.uuid === row.uuid ? normalizePriceRow({ ...r, data_status: status }, marketLookupRef.current, speciesLookupRef.current) : r
        )
      );
      pushWorkflowNotification({
        moduleKey: 'markets', roleCan: PRICE_ROLE_CAN, newStatus: status,
        actorRoleId: roleId, recordLabel: row.market_label, notes,
      });
    } catch (e) {
      setFetchError(parseError(e, `Failed to update ${status}.`));
      throw e;
    } finally {
      setQuickBusy('');
    }
  }, [roleId, pushWorkflowNotification]);

  const handleQuickSubmit   = useCallback((row) => handleQuickStatusSave(row, 'submitted'), [handleQuickStatusSave]);
  const handleQuickValidate = useCallback((row) => handleQuickStatusSave(row, 'validated'), [handleQuickStatusSave]);
  const handleQuickApprove  = useCallback((row) => handleQuickStatusSave(row, 'approved'),  [handleQuickStatusSave]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    await handleQuickStatusSave(rejectTarget, 'rejected', notes);
    setRejectTarget(null);
  }, [rejectTarget, handleQuickStatusSave]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy('Deleting...');
    try {
      await marketPricesApi.remove(deleteTarget.uuid);
      setRecords((prev) => prev.filter((r) => r.uuid !== deleteTarget.uuid));
      setDeleteTarget(null);
    } catch (e) {
      setFetchError(parseError(e, 'Failed to delete record.'));
    } finally {
      setQuickBusy('');
    }
  }, [deleteTarget]);

  const openMarketPriceRecord = useCallback((mode, row) => {
    const uuid = row?.uuid ?? row?.id;
    if (!uuid) return;
    navigate(`/markets-trade/${mode}/${uuid}?subModule=prices`, { state: { row } });
  }, [navigate]);

  const handleRowAction = useCallback((action, row, meta) => {
    const childAction = meta?.button?.getAttribute?.('data-child-action');
    if (childAction === 'view') {
      openMarketPriceRecord('view', row);
      return;
    }
    if (childAction === 'edit') {
      openMarketPriceRecord('edit', row);
      return;
    }

    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action === 'view') setActionMenu({ row, rect });
  }, [openMarketPriceRecord]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── State filter bar — hidden for R05 ── */}
      {!isR05 && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <FilterSelect
            value={draftState}
            onChange={(value) => { setDraftState(value); setStateFilter(value); }}
            options={stateOptions}
            defaultLabel="National (All States)"
          />
          <button
            onClick={handleApply}
            className="h-10 rounded-lg bg-green-100 px-6 text-sm font-semibold text-green-700 transition-colors hover:bg-green-200"
          >
            Apply
          </button>
        </div>
      )}

      {/* ── Search + Create row ── */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search market prices..."
            className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
          />
        </div>
        {onCreateNew && (
          <button
            onClick={onCreateNew}
            className="h-10 rounded-lg bg-green-700 px-5 text-sm font-semibold text-white transition-colors hover:bg-green-800"
          >
            + Create New
          </button>
        )}
      </div>

      {/* ── Status banners ── */}
      {refError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{refError}</div>
      )}
      {(refLoading || loadingRecords) && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600 shadow-sm">
          Loading market prices...
        </div>
      )}
      {fetchError && (
        <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{fetchError}</span>
          <button
            onClick={() => { setFetchError(''); loadRecords(); }}
            className="ml-4 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
          >
            Retry
          </button>
        </div>
      )}
      {/* ── Market Price table (NlimsDataTable) — sorting + column filter hamburgers built-in ── */}
      <NlimsDataTable
        tableId="market-prices-table"
        columns={TABLE_COLUMNS}
        data={displayRecords}
        serverSide={false}
        hideSearch
        searchQuery={search}
        pageLength={25}
        defaultSort={[1, 'desc']}
        getChildRowHtml={renderMarketPriceChildRow}
        onRowAction={handleRowAction}
      />

      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.marketPrices}
          onClose={() => setActionMenu(null)}
          onView={() => { const r = actionMenu.row; setActionMenu(null); navigate(`/markets-trade/view/${r.uuid}?subModule=prices`, { state: { row: r } }); }}
          onEdit={() => { const r = actionMenu.row; setActionMenu(null); navigate(`/markets-trade/edit/${r.uuid}?subModule=prices`, { state: { row: r } }); }}
          onSubmit={() => { const r = actionMenu.row; setActionMenu(null); handleQuickSubmit(r); }}
          onValidate={() => { const r = actionMenu.row; setActionMenu(null); handleQuickValidate(r); }}
          onApprove={() => { const r = actionMenu.row; setActionMenu(null); handleQuickApprove(r); }}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={deleteTarget.market_label}
          noun="record"
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={!!quickBusy}
        />
      )}
    </div>
  );
}
